'use client';

import styled, { css } from 'styled-components';

import { breakpoints, colors } from '@/styles/theme';

export const HomeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  max-width: 1320px;
  gap: 144px;
  margin: 0 auto;
  padding: 0 24px;

  & > * {
    width: 100%;
  }

  @media (max-width: ${breakpoints.laptop}) {
    padding: 0 32px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0 16px;
  }
`;

export const NarrowWrapper = styled.div`
  width: 100%;
  max-width: 1024px;

  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0 16px;
  }
`;

export const Main = styled.main`
  display: flex;
  flex-direction: column;

  width: 100%;
  max-width: 1720px;

  margin: ${({ $headerHeight }) => $headerHeight || 64}px auto 0;
  padding: 0 8px 64px;

  @media (max-width: ${breakpoints.laptop}) {
    padding: 0 8px 32px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0 8px 16px;
  }
`;

export const Caption = styled.div`
  margin-top: 10px;

  font-size: 0.9em;
  line-height: 1.4;
  color: ${colors.gray5};
  font-style: italic;
  text-align: center;

  &::before {
    content: '— ';
    font-weight: bold;
  }

  &::after {
    content: ' —';
    font-weight: bold;
  }
`;

export const Blockquote = styled.blockquote`
  font-size: 1.4em;
  margin: 50px 0;
  font-family: Georgia, Times, 'Times New Roman', serif;
  font-style: italic;
  color: ${colors.gray5};
  line-height: 1.6;
  position: relative;

  padding: 1.2em 32px 1.2em 72px;
  border-left: 8px solid ${colors.blue};
  &:before {
    font-family: Arial;
    content: '“';
    color: ${colors.blue};
    font-size: 4em;
    position: absolute;
    left: 10px;
    top: -10px;
  }

  ${({ $notQuote }) =>
    $notQuote &&
    css`
      padding: 0 64px;
      margin: 0 0 32px;

      &:before {
        content: '';
      }
    `}
`;

export const PostMediaWrap = styled.div`
  margin: 32px auto;
`;

export const PageTitle = styled.h1`
  display: flex;
  justify-content: flex-end;

  padding: 0 64px 32px;
  margin: 32px 0 64px;

  font-size: 8rem;
  color: ${({ $color }) => colors[$color] || colors.blue};
  border-bottom: 1px solid ${({ $color }) => colors[$color] || colors.blue};
  opacity: 0.5;

  ${({ $small }) => $small && 'font-size: 3rem;'}
  ${({ $medium }) => $medium && 'font-size: 6rem;'}

  ${({ $naked }) =>
    $naked &&
    css`
      border: none;
      margin: 32px 0 0;
    `}

    @media (max-width: ${breakpoints.desktop}) {
    font-size: 6rem;
    ${({ $small }) => $small && 'font-size: 2.5rem;'}
    ${({ $medium }) => $medium && 'font-size: 5rem;'}

    margin: 24px 0 48px;
  }

  @media (max-width: ${breakpoints.laptop}) {
    font-size: 6rem;
    ${({ $small }) => $small && 'font-size: 2.5rem;'}
    ${({ $medium }) => $medium && 'font-size: 4rem;'}

    margin: 20px 0 32px;
  }

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 4rem;
    ${({ $small }) => $small && 'font-size: 2rem;'}
    ${({ $medium }) => $medium && 'font-size: 3.5rem;'}

    margin: 16px 0 24px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 2.7rem;
    ${({ $small }) => $small && 'font-size: 1.5rem;'}
    ${({ $medium }) => $medium && 'font-size: 2.5rem;'}

    padding: 12px;
    justify-content: center;
  }
`;

export const Title = styled.h1`
  margin-bottom: ${({ $noMargin }) => ($noMargin ? 0 : '32px')};

  text-align: ${({ $align }) => ($align ? $align : 'center')};
  color: ${colors.blue};
  opacity: 0.7;

  ${({ $small }) => $small && 'font-size: 1.5rem; margin-bottom: 16px;'}
  ${({ $medium }) => $medium && 'font-size: 2.5rem; margin-bottom: 24px;'}

  @media (max-width: ${breakpoints.desktop}) {
    margin-bottom: ${({ $noMargin }) => ($noMargin ? 0 : '24px')};

    font-size: 3rem;
    ${({ $small }) => $small && 'font-size: 1.3rem;'}
    ${({ $medium }) => $medium && 'font-size: 2rem;'}
  }

  @media (max-width: ${breakpoints.laptop}) {
    margin-bottom: ${({ $noMargin }) => ($noMargin ? 0 : '16px')};

    font-size: 2.7rem;
    ${({ $small }) => $small && 'font-size: 1.2rem;'}
    ${({ $medium }) => $medium && 'font-size: 1.8rem;'}
  }

  @media (max-width: ${breakpoints.tablet}) {
    margin-bottom: ${({ $noMargin }) => ($noMargin ? 0 : '12px')};

    font-size: 2.3rem;
    ${({ $small }) => $small && 'font-size: 1rem;'}
    ${({ $medium }) => $medium && 'font-size: 1.7rem;'}
  }

  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: ${({ $noMargin }) => ($noMargin ? 0 : '8px')};

    font-size: 1.9rem;
    ${({ $small }) => $small && 'font-size: 0.9rem;'}
    ${({ $medium }) => $medium && 'font-size: 1.6rem;'}
  }
`;

export const FormControl = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1;
  min-width: 0;
`;

export const Label = styled.div`
  display: flex;

  padding: 10px 0;

  color: ${colors.light};
`;

export const Input = styled.input`
  height: 40px;
  padding: 10px;

  color: ${colors.dark};
  background-color: ${colors.light};

  border-radius: 16px;
  outline: none;

  &::placeholder {
    color: ${colors.gray5};
    font-size: 0.8rem;
  }

  ${({ $dark }) =>
    $dark &&
    css`
      color: ${colors.light};
      background-color: #f6fafd33;

      &::placeholder {
        color: ${colors.blue};
      }
    `}

  border: 2px solid
    ${({ $status }) =>
    $status === 'valid' ? colors.green : $status === 'invalid' ? colors.red : 'transparent'};

  ${({ as }) =>
    as === 'textarea' &&
    css`
      height: auto;
      resize: vertical;
      line-height: 1.5;
      resize: none;
    `}
`;

export const Validation = styled.div`
  margin: 10px auto 0 auto;

  a {
    text-decoration: underline;
  }
`;

export const Message = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  text-align: center;
  font-size: 1.2rem;
  line-height: 1.4;
  font-weight: 500;
`;
